'use client';

import React from 'react';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { type Theme } from '@mui/material/styles';

// eslint-disable-next-line import/no-extraneous-dependencies
import { ErrorCode } from '@watershed/errors/ErrorRegistry';
import CircleRemoveIcon from '@watershed/icons/components/CircleRemove';
import HelpIcon from '@watershed/icons/components/Help';
import SyncIcon from '@watershed/icons/components/Sync';
import WarningIcon from '@watershed/icons/components/Warning';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import { conicRotate } from '@watershed/style/keyframes';
import mixinSx from '@watershed/style/mixinSx';

export interface FlexiError extends Error {
  code?: string;
}

const colorForError = (error: FlexiError, theme: Theme) => {
  let iconColor: string;
  let borderColor: string;
  let outlineColor: string;

  switch (error.code) {
    case 'UNAUTHENTICATED' satisfies ErrorCode:
    case 'CROSS_ORG' satisfies ErrorCode:
    case 'GRAPHQL_VALIDATION_FAILED' satisfies ErrorCode:
    case 'DATA_RESPONSE_TOO_LARGE' satisfies ErrorCode:
    case 'MESSAGE_LOADING_ERROR' satisfies ErrorCode:
      iconColor = theme.palette.warning.dark;
      borderColor = theme.palette.marigold20;
      outlineColor = theme.palette.marigold;
      break;
    case 'NOT_FOUND' satisfies ErrorCode:
      iconColor = theme.palette.grey70;
      borderColor = theme.palette.grey30;
      outlineColor = theme.palette.grey40;
      break;
    case 'FORBIDDEN' satisfies ErrorCode:
      iconColor = theme.palette.error.dark;
      borderColor = theme.palette.sun20;
      outlineColor = theme.palette.sun30;
      break;
    default:
      iconColor = theme.palette.error.dark;
      borderColor = theme.palette.sun20;
      outlineColor = theme.palette.sun30;
      break;
  }
  return { icon: iconColor, border: borderColor, outline: outlineColor };
};

// Icon definitions remain dependent on app-dashboard error codes for now.
// Ideally, these codes might also be shared or defined in a common place
// to avoid duplication.
const getIconsForError = (error: FlexiError) => {
  switch (error.code) {
    case 'UNAUTHENTICATED' satisfies ErrorCode:
      return { icon: WarningIcon, testId: TestIds.ErrorWarningIcon };
    case 'CROSS_ORG' satisfies ErrorCode:
      return { icon: CircleRemoveIcon, testId: TestIds.ErrorErrorIcon };
    case 'GRAPHQL_VALIDATION_FAILED' satisfies ErrorCode:
      return { icon: SyncIcon, testId: TestIds.ErrorSyncIcon };
    case 'DATA_RESPONSE_TOO_LARGE' satisfies ErrorCode:
      return { icon: WarningIcon, testId: TestIds.ErrorWarningIcon };
    case 'MESSAGE_LOADING_ERROR' satisfies ErrorCode:
      return { icon: WarningIcon, testId: TestIds.ErrorWarningIcon };
    case 'FORBIDDEN' satisfies ErrorCode:
      return { icon: CircleRemoveIcon, testId: TestIds.ForbiddenIcon };
    case 'NOT_FOUND' satisfies ErrorCode:
      return { icon: HelpIcon, testId: TestIds.NotFoundIcon };
    default: // Includes generic/unexpected errors
      return { icon: WarningIcon, testId: TestIds.ErrorWarningIcon };
  }
};

const IconWrapper = ({
  error,
  testId,
}: {
  error: FlexiError;
  testId?: string;
}) => {
  const { icon: IconComponent } = getIconsForError(error);

  return (
    <Box
      display="flex"
      justifyContent="center"
      position="relative"
      overflow="hidden"
      sx={(theme) => {
        const { border: borderColor, outline: outlineColor } = colorForError(
          error,
          theme
        );
        return {
          p: 1,
          border: `1px solid ${borderColor}`,
          outline: `1px dotted ${outlineColor}`,
          outlineOffset: '2px',
          backgroundColor: theme.palette.common.white,
          mb: 1,
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: `conic-gradient(from 0deg, ${borderColor}10, ${outlineColor}90, transparent 100%)`,
            animation: `${conicRotate} 2s linear infinite`,
            zIndex: 0,
          },
        };
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(12px)',
          background: (theme) => alpha(theme.palette.common.white, 0.6),
          width: '100%',
          height: '100%',
        }}
      />
      <IconComponent
        size={18}
        sx={{ zIndex: 2 }}
        data-testid={testId}
        color={(theme) => {
          const { icon: iconColor } = colorForError(error, theme);
          return iconColor;
        }}
      />
    </Box>
  );
};

const ErrorRoot = ({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId?: string;
}) => (
  <Stack direction="column" alignItems="center" gap={1} data-testid={testId}>
    {children}
  </Stack>
);

const ErrorIcon = ({
  error,
  testId,
}: {
  error: FlexiError;
  testId?: string;
}) => {
  const { testId: defaultTestId } = getIconsForError(error);
  return <IconWrapper error={error} testId={testId ?? defaultTestId} />;
};

const ErrorTitle = ({
  children,
  ...props
}: React.ComponentProps<typeof Typography>) => (
  <Typography variant="h3" {...props}>
    {children}
  </Typography>
);

const ErrorDescription = ({
  children,
  ...props
}: React.ComponentProps<typeof Typography>) => (
  <Typography
    variant="body2"
    {...props}
    sx={mixinSx({ textWrap: 'balance' }, props.sx)}
  >
    {children}
  </Typography>
);

export const Error = {
  Icon: ErrorIcon,
  Title: ErrorTitle,
  Description: ErrorDescription,
  Root: ErrorRoot,
};
